import { ClassNames, css, useTheme } from '@emotion/react'
import { ClassNames as DayPickerClassNames } from 'react-day-picker'
import { ReactNode } from 'react-markdown'

import { MooncakeTheme } from '../../theme'
import { useDropdownStatus } from './DatePickerDropdownContext'

type Props = {
  children: (classNames: DayPickerClassNames) => ReactNode
}

export const buttonLikeBase = (theme: MooncakeTheme) => css`
  ${theme.typography.bodyText};
  color: ${theme.colors.label};
  background: ${theme.colors.secondaryButtonBackground};
  border-radius: 0.5rem;
  border: 2px solid transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  outline: 0;
`

export const buttonLikeActive = (theme: MooncakeTheme) => css`
  background: ${theme.colors.secondaryButtonActive};
`

export const buttonLikeSelected = (theme: MooncakeTheme) => css`
  border: 2px solid ${theme.colors.primary};
  border-radius: 0.5rem;
  font-weight: ${theme.weights.medium};
  background: none;
`

export const buttonLikeFocus = (theme: MooncakeTheme) => css`
  outline-style: solid;
  outline-width: 0.5rem;
  outline-color: ${theme.colors.primary}33;
`

export const visuallyHidden = css`
  border: 0;
  clip: rect(0 0 0 0);
  height: auto;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`

export const DatePickerClassNames = ({ children }: Props) => {
  const theme = useTheme()
  const dropdownStatus = useDropdownStatus()

  const gap = theme.pxToRem(1)
  const dayInnerSize = '1.5rem'
  const daySize = '2.5rem'

  return (
    <ClassNames>
      {({ css }) =>
        children({
          button: css``,
          day: css`
            ${buttonLikeBase(theme)}
            background: none;
            width: ${daySize};
            height: ${daySize};

            ${theme.selectors.keyboard} &:focus {
              ${buttonLikeFocus(theme)}
            }
          `,
          day_selected: css`
            // increase specifificty to always be greater than above "day"
            body & {
              ${buttonLikeSelected(theme)}
            }
          `,
          day_today: css`
            font-weight: ${theme.weights.medium};
          `,
          head_cell: css`
            ${theme.typography.identifier};
            height: 1.75rem;
            text-align: center;
            vertical-align: top;
            color: ${theme.colors.placeholderLabel};
          `,
          cell: css`
            :not(:first-of-type) {
              padding-left: ${gap};
            }
            :not(:last-of-type) {
              padding-right: ${gap};
            }
          `,
          row: css`
            :not(:first-of-type) {
              border-top: ${gap} solid transparent;
            }
            :not(:last-of-type) {
              border-bottom: ${gap} solid transparent;
            }
          `,
          caption: css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: ${theme.spacing.s};
            align-items: center;
            justify-content: space-between;
            margin-bottom: ${theme.spacing.l};
          `,
          caption_label: css`
            ${visuallyHidden}
          `,
          caption_dropdowns: css`
            grid-column: span 3; // align exactly with three dropdown options
            width: 100%;
            display: flex;
          `,
          nav: css`
            display: flex;
            gap: calc(${theme.spacing.xxs} / 2);
            margin-left: calc(${theme.spacing.s} * -1); // erase grid colum gap
            padding-left: 0.25rem;
            justify-content: space-between;
          `,
          nav_button: css`
            color: ${theme.colors.tertiaryIcon};
            background: none;
            border: none;
            padding: 0;
            width: 2rem;
            height: 2rem;
            cursor: pointer;
            transform-origin: center center;

            :disabled {
              color: ${theme.colors.disabledIcon};
              cursor: default;
            }
          `,
          nav_button_next: css`
            transform: rotate(90deg);
          `,
          nav_button_previous: css`
            transform: rotate(-90deg);
          `,
          table: css`
            margin-left: calc((${daySize} - ${dayInnerSize}) / -2);
            margin-right: calc((${daySize} - ${dayInnerSize}) / -2);
            margin-bottom: calc((${daySize} - ${dayInnerSize}) / -2);

            ${dropdownStatus.open &&
            // hides table while preserving width
            css`
              display: grid;
              grid-auto-rows: 0;
              visibility: hidden;
              margin-bottom: 0;
            `}
          `,
        })
      }
    </ClassNames>
  )
}
